/**
 * 移动端导航菜单：使用 Sheet 组件实现侧滑抽屉式导航。
 */

import type { ReactElement } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Bot,
  CreditCard,
  Megaphone,
  History,
  LayoutGrid,
  Menu,
  ReceiptText,
  Server,
  Sparkles,
  User,
  Zap,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores'
import type { ComputeType } from '@/types/auth'

const CLIENT_ASSISTANT_NAV_HASH = '#client-assistant-download' as const
const CLIENT_ASSISTANT_NAV_HREF = `/pricing${CLIENT_ASSISTANT_NAV_HASH}` as const

/**
 * 根据算力余额映射进度条宽度。
 * @param credits 算力余额
 * @returns Tailwind width className
 */
function getCreditBarWidthClassName(credits: number): string {
  if (credits >= 800) {
    return 'w-[100%]'
  }
  if (credits >= 500) {
    return 'w-[80%]'
  }
  if (credits >= 300) {
    return 'w-[60%]'
  }
  if (credits >= 150) {
    return 'w-[40%]'
  }
  if (credits >= 60) {
    return 'w-[25%]'
  }
  return 'w-[12%]'
}

/**
 * 获取算力类型展示文案
 * @param computeType 算力类型
 * @returns 展示文案
 */
function getComputeTypeLabel(computeType: ComputeType | undefined): string {
  return computeType === 'exclusive' ? '专享算力' : '共享算力'
}

/**
 * 移动端导航菜单组件。
 * @returns ReactElement
 */
export function MobileMenu(): ReactElement {
  const location = useLocation()
  const user = useAuthStore((state) => state.user)
  const credits = user?.company?.balance ?? 0
  const computeTypeLabel = getComputeTypeLabel(user?.company?.computeType)
  const isClientAssistantNavActive =
    location.pathname === '/pricing' && location.hash === CLIENT_ASSISTANT_NAV_HASH

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden">
          <Menu className="h-5 w-5" />
          <span className="sr-only">打开菜单</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[280px] p-0">
        <SheetHeader className="border-b px-6 py-4">
          <SheetTitle className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-lg shadow-primary/20">
              <Sparkles className="h-5 w-5" />
            </div>
            <span className="font-bold tracking-tight">月汐AI</span>
          </SheetTitle>
        </SheetHeader>

        <nav className="flex flex-col gap-1 p-4">
          {[
            { icon: LayoutGrid, label: '工作流', href: '/workflows' },
            { icon: Megaphone, label: '系统更新', href: '/updates' },
            { icon: CreditCard, label: '购买算力', href: '/pricing' },
            { icon: Bot, label: '采集助手下载', href: CLIENT_ASSISTANT_NAV_HREF },
            ...(user?.company?.computeType === 'exclusive'
              ? [{ icon: Server, label: '算力管理', href: '/exclusive-compute' }]
              : []),
            { icon: History, label: '生图记录', href: '/records/generations' },
            { icon: Sparkles, label: '工具记录', href: '/records/tools' },
            { icon: Zap, label: '算力流水', href: '/records/billing' },
            { icon: ReceiptText, label: '充值记录', href: '/records/recharges' },
            { icon: User, label: '个人中心', href: '/me' },
          ].map((item) => {
            const isHomeWorkflow: boolean =
              location.pathname === '/' && item.href === '/workflows'
            const isPricingNavItem: boolean = item.href === '/pricing'
            const isClientAssistantNavItem: boolean = item.href === CLIENT_ASSISTANT_NAV_HREF
            const isActive: boolean =
              isHomeWorkflow ||
              (isClientAssistantNavItem
                ? isClientAssistantNavActive
                : isPricingNavItem
                  ? location.pathname === '/pricing' && !isClientAssistantNavActive
                  : location.pathname === item.href ||
                    location.pathname.startsWith(`${item.href}/`))

            return (
              <SheetClose asChild key={item.href}>
                <Link
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              </SheetClose>
            )
          })}
        </nav>

        <div className="mt-auto border-t p-4">
          <div className="rounded-xl bg-muted/50 p-4">
            {user === null ? (
              <div className="space-y-3">
                <p className="text-xs font-semibold text-foreground">
                  登录后同步记录与算力
                </p>
                <SheetClose asChild>
                  <Button asChild size="sm" className="w-full">
                    <Link to="/login">登录 / 注册</Link>
                  </Button>
                </SheetClose>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-foreground">
                    {computeTypeLabel}
                  </p>
                  <span className="rounded-full bg-primary/20 px-1.5 py-0.5 text-[10px] text-primary">
                    算力
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-border">
                  <div
                    className={cn(
                      'h-full bg-primary',
                      getCreditBarWidthClassName(credits)
                    )}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  算力：{credits}
                </p>
                <SheetClose asChild>
                  <Button asChild size="sm" variant="secondary" className="w-full">
                    <Link to="/pricing">去充值</Link>
                  </Button>
                </SheetClose>
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
